import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Facebook, Twitter, Linkedin, Mail, Link as LinkIcon, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { useLanguage } from "@/contexts/LanguageContext";

interface ShareDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const ShareDialog = ({ open, onOpenChange }: ShareDialogProps) => {
  const { t } = useLanguage();
  const [copied, setCopied] = useState(false);
  
  const currentUrl = window.location.href;
  const pageTitle = document.title || "Fabbriche Toscane";

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(currentUrl);
      setCopied(true);
      toast.success(t('share.linkCopied') || "Link copiato!");
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      toast.error(t('share.linkError') || "Errore durante la copia");
    }
  };

  const shareViaEmail = () => {
    const subject = encodeURIComponent(pageTitle);
    const body = encodeURIComponent(`${t('share.emailBody') || 'Guarda questa pagina'}: ${currentUrl}`);
    window.open(`mailto:?subject=${subject}&body=${body}`, '_blank');
  };

  const shareOnFacebook = () => {
    window.open(
      `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(currentUrl)}`,
      '_blank',
      'width=600,height=400'
    );
  };

  const shareOnTwitter = () => {
    const text = encodeURIComponent(pageTitle);
    window.open(
      `https://twitter.com/intent/tweet?url=${encodeURIComponent(currentUrl)}&text=${text}`,
      '_blank',
      'width=600,height=400'
    );
  };

  const shareOnLinkedIn = () => {
    window.open(
      `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(currentUrl)}`,
      '_blank',
      'width=600,height=400'
    );
  };

  const shareOnWhatsApp = () => {
    const text = encodeURIComponent(`${pageTitle} ${currentUrl}`);
    window.open(
      `https://wa.me/?text=${text}`,
      '_blank'
    );
  };

  const useNativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: pageTitle,
          url: currentUrl,
        });
      } catch (err) {
        console.log('Share cancelled or error:', err);
      }
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t('share.title') || 'Condividi questa pagina'}</DialogTitle>
          <DialogDescription>
            {t('share.description') || 'Scegli come condividere questa pagina'}
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4">
          {/* Copy Link */}
          <div className="flex items-center space-x-2">
            <Input
              readOnly
              value={currentUrl}
              className="flex-1"
            />
            <Button
              type="button"
              size="sm"
              onClick={copyToClipboard}
              className="shrink-0"
            >
              <LinkIcon className="h-4 w-4 mr-2" />
              {copied ? (t('share.copied') || 'Copiato!') : (t('share.copy') || 'Copia')}
            </Button>
          </div>

          {/* Native Share (Mobile) */}
          {navigator.share && (
            <Button
              onClick={useNativeShare}
              variant="outline"
              className="w-full"
            >
              {t('share.native') || 'Condividi'}
            </Button>
          )}

          {/* Social Media Buttons */}
          <div className="grid grid-cols-2 gap-2">
            <Button
              onClick={shareViaEmail}
              variant="outline"
              className="w-full"
            >
              <Mail className="h-4 w-4 mr-2" />
              Email
            </Button>
            
            <Button
              onClick={shareOnWhatsApp}
              variant="outline"
              className="w-full"
            >
              <MessageCircle className="h-4 w-4 mr-2" />
              WhatsApp
            </Button>

            <Button
              onClick={shareOnFacebook}
              variant="outline"
              className="w-full"
            >
              <Facebook className="h-4 w-4 mr-2" />
              Facebook
            </Button>

            <Button
              onClick={shareOnTwitter}
              variant="outline"
              className="w-full"
            >
              <Twitter className="h-4 w-4 mr-2" />
              Twitter
            </Button>

            <Button
              onClick={shareOnLinkedIn}
              variant="outline"
              className="w-full col-span-2"
            >
              <Linkedin className="h-4 w-4 mr-2" />
              LinkedIn
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
